import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { RotationTreeEnum } from 'src/enums/rotationTreeEnum';
import { IRotationNode } from 'src/types/ViewModel/IRotationNode';
import { EventType } from 'src/types/ViewModel/EventType';

/**
 * Utility functions for handling retroactive cargo event modifications after vessel departure (SOSP).
 * This enables super users to add/edit cargo-level events without requiring deletion of SOSP and subsequent sea events.
 */

/**
 * Checks if an event is at the cargo operation level
 */
export const isCargoLevelEvent = (event: TankersEvent | null): boolean => {
    if (!event) return false;
    return event.cargoOperationId !== null && event.cargoOperationId !== undefined && event.eventLevel === RotationTreeEnum.CargoOperation;
};

/**
 * Checks if an event is SOSP or a sea event
 */
export const isSospOrSeaEvent = (event: TankersEvent | null): boolean => {
    if (!event) return false;
    return event.eventTypeId === 3 || event.eventLevel === RotationTreeEnum.Sea;
};

/**
 * Determines if retroactive cargo event modification is allowed.
 * This bypasses normal validation for cargo-level events when:
 * - The event is at cargo operation level
 * - Retroactive mode is enabled (when used in UI context)
 *
 * @param checkEvent - The event being validated
 * @param blockingEvent - The event that would normally block the operation
 * @param eventArray - All events for validation context
 * @param isRetroactiveModeEnabled - Optional flag to check if retroactive mode is enabled in UI
 */
export const canBypassValidationForRetroactiveCargo = (
    checkEvent: TankersEvent,
    blockingEvent: TankersEvent | null,
    eventArray: TankersEvent[],
    isRetroactiveModeEnabled?: boolean
): boolean => {
    // If retroactive mode flag is explicitly provided and is false, do not bypass
    // This allows UI to control when bypass is active
    if (isRetroactiveModeEnabled !== undefined && !isRetroactiveModeEnabled) {
        return false;
    }

    // Only allow bypass for cargo-level events
    if (!isCargoLevelEvent(checkEvent)) {
        return false;
    }

    // When retroactive mode is enabled, bypass all validation restrictions for cargo events
    return true;
};

/**
 * Gets a user-friendly message for retroactive cargo event operations
 */
export const getRetroactiveCargoEventMessage = (): string => {
    return 'Retroactive cargo mode enabled - validation bypassed for cargo events';
};

/**
 * Checks if the current event type is valid for retroactive modification
 */
export const isValidRetroactiveCargoEventType = (eventType: EventType | null): boolean => {
    if (!eventType || !eventType.eventTypeGroup) return false;

    // Only allow cargo operation level events
    return eventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId === RotationTreeEnum.CargoOperation;
};

/**
 * Filters events to get cargo-level events for a specific port call
 */
export const getCargoEventsForPortCall = (eventArray: TankersEvent[], portCallId: number): TankersEvent[] => {
    return eventArray.filter((ev) => ev.portCallId === portCallId && isCargoLevelEvent(ev) && !ev.deleted);
};

/**
 * Checks if an event array has any cargo events that would need retroactive modification
 */
export const hasRetroactiveCargoEvents = (eventArray: TankersEvent[], portCallId: number): boolean => {
    const hasCargoEvents = getCargoEventsForPortCall(eventArray, portCallId).length > 0;
    return hasCargoEvents;
};
